package info.rezident.app.pages

import info.rezident.app.core.AppTracking
import info.rezident.app.core.Logger
import info.rezident.app.data.quiz.*
import info.rezident.app.data.user.UserRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

/**
 * ViewModel for the Quiz feature.
 * This class manages quiz data and provides methods to get questions for each step.
 */
class QuizViewModel {
    private val quizTemplateRepository = QuizTemplateRepository
    private val userQuizRepository = UserQuizRepository

    private val _isLoading = MutableStateFlow(true)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _steps = MutableStateFlow<List<QuizStep>>(emptyList())
    val steps: StateFlow<List<QuizStep>> = _steps.asStateFlow()

    private val _questions = MutableStateFlow<List<QuizQuestion>>(emptyList())
    val questions: StateFlow<List<QuizQuestion>> = _questions.asStateFlow()

    // Add state to track the current step
    private val _currentStepId = MutableStateFlow(1)
    val currentStepId: StateFlow<Int> = _currentStepId.asStateFlow()

    // Expose saving state from repository
    val isSavingQuiz: StateFlow<Boolean> = userQuizRepository.isSavingQuiz

    private val viewModelScope = CoroutineScope(Dispatchers.Default)

    init {
        // Fetch questions and steps from repository (default to current template)
        viewModelScope.launch {
            quizTemplateRepository.fetchQuestions()
        }

        viewModelScope.launch {
            quizTemplateRepository.fetchSteps()
        }

        // Combine questions and steps flows to determine loading state
        viewModelScope.launch {
            combine(quizTemplateRepository.questions, quizTemplateRepository.steps) { questions, steps ->
                questions.isNotEmpty() && steps.isNotEmpty()
            }.collectLatest { isLoaded ->
                delay(500)
                _isLoading.value = !isLoaded
            }
        }

        // Collect questions flow
        viewModelScope.launch {
            quizTemplateRepository.questions.collectLatest { questionsList ->
                _questions.value = questionsList
            }
        }

        // Collect steps flow
        viewModelScope.launch {
            quizTemplateRepository.steps.collectLatest { stepsList ->
                _steps.value = stepsList
            }
        }
    }

    /**
     * Loads questions and steps for a specific template ID.
     * Used by Summary page to display the correct template when viewing old quizzes.
     *
     * @param templateId The template ID to load
     */
    suspend fun loadTemplateById(templateId: Int) {
        _isLoading.value = true
        try {
            quizTemplateRepository.fetchQuestions(templateId)
            quizTemplateRepository.fetchSteps(templateId)
        } finally {
            _isLoading.value = false
        }
    }

    /**
     * Updates the quiz with new answers.
     * If answers for the same questions already exist, they will be replaced.
     *
     * @param newAnswer The answer to add or update
     */
    fun updateAnswers(newAnswer: UserAnswer) {
        val currentAnswers = userQuizRepository.userAnswers.value
        val updatedAnswers = currentAnswers.copy(
            answers = (listOf(newAnswer) + currentAnswers.answers)
                .distinctBy { answer -> answer.questionId }
        )
        userQuizRepository.updateUserAnswers(updatedAnswers)

        // Track question answered event
        AppTracking.logEvent(
            AppTracking.EVENT_QUIZ_QUESTION_ANSWERED,
            mapOf(
                AppTracking.PARAM_TEMPLATE_ID to currentAnswers.templateId.toString(),
                AppTracking.PARAM_QUESTION_ID to newAnswer.questionId.toString()
            )
        )

        // Resolve and persist academicYear centrally (cache + analytics + Firestore merge)
        viewModelScope.launch {
            UserRepository.resolveAndSetAcademicYear(userQuizRepository.userAnswers.value, _questions.value)
        }
    }

    fun updateCurrentStep(stepId: Int) {
        _currentStepId.value = stepId
    }

    suspend fun isStepCompleted(stepId: Int): Boolean {
        // Check if all mandatory questions are answered
        val mandatoryQuestions = questions.firstOrNull { it.isNotEmpty() }
            ?.filter { it.stepId == stepId && it.mandatory }
            ?.map { it.id }

        if (mandatoryQuestions == null) {
            return false
        }

        val answeredQuestions = userQuizRepository.userAnswers.value.answers
            .filter {
                it.answerIds?.isNotEmpty() == true
                        || (it.openAnswer?.isNotBlank() == true && it.openAnswer.length >= 5)
                        || it.fiveScaleAnswer != null
            }
            .map { it.questionId }

        return answeredQuestions.containsAll(mandatoryQuestions) && answeredQuestions.isNotEmpty()
    }

    /**
     * Gets the step object for a given index
     */
    fun getStep(stepIndex: Int): QuizStep? {
        return _steps.value.firstOrNull { it.id == stepIndex }
    }

    /**
     * Returns step title for the given step index
     */
    fun getStepTitle(stepIndex: Int): String {
        val step = getStep(stepIndex)
        return when {
            step != null -> step.title
            stepIndex == 4 -> "Rezumatul Quiz-ului"
            stepIndex == 5 -> "Recomandări de specialități"
            stepIndex == 6 -> "Ești aproape"
            stepIndex == 7 -> "Contul tău"
            else -> ""
        }
    }

    /**
     * Triggers saving when Summary step loads (non-blocking)
     */
    fun startQuizSaving() {
        userQuizRepository.startSavingQuizAnswersInBackground(userQuizRepository.userAnswers.value)
    }

    /**
     * Waits for quiz saving to complete and returns document ID for navigation.
     * This method contains the business logic for ensuring we have a document ID before navigation.
     * Uses Flow to wait for the document ID to be available.
     */
    suspend fun waitForQuizSavingAndGetDocumentId(): String? {
        return try {
            // Wait for either the saving to complete or get the existing document ID
            userQuizRepository.savedQuizDocumentId
                .first { documentId ->
                    // We have a document ID or saving has finished (not loading anymore)
                    documentId != null || !userQuizRepository.isSavingQuiz.value
                }
                ?.also { documentId ->
                    // Clear user's answers so that they can't go back and "pseudo-edit" their Quiz...
                    clear()
                    Logger.log("Quiz answers successfully saved to Firestore with document ID: $documentId")
                } ?: run {
                Logger.error("Failed to get quiz document ID after saving completed")
                null
            }
        } catch (e: Exception) {
            Logger.error("Error waiting for quiz saving: ${e.message}")
            null
        }
    }

    fun clear() {
        userQuizRepository.clearQuizUserData()
        _currentStepId.update {
            1
        }
    }
}